import { useMemo } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { TagsV2 } from '@frontend/api-tag';
import { Icon } from '@frontend/icons';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Chip } from '@frontend/design-system';
import { useInboxFilterStatusLabels, useInboxFiltersShallowStore } from '../stores/inbox-store';
import { HiddenFilterKeys, InboxFilter } from '../types';

type InboxFiltersStatusProps = {
  hideFilters?: HiddenFilterKeys;
};

export const InboxFiltersStatus = ({ hideFilters }: InboxFiltersStatusProps) => {
  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const { inboxFilters, setInboxFilters } = useInboxFiltersShallowStore('inboxFilters', 'setInboxFilters');
  const {
    data: { allLocationPhones },
  } = InboxLocationPhones.Hooks.useLocationPhones();
  const statusLabels = useInboxFilterStatusLabels();
  const tagsListQuery = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
  });
  const filtersToHide = useMemo(
    () =>
      Object.entries(inboxFilters).reduce<NonNullable<HiddenFilterKeys>>(
        (acc, [key, value]) => {
          if (acc.includes(key as InboxFilter)) return acc;
          if (!value.length) acc.push(key as InboxFilter);
          return acc;
        },
        [...(hideFilters ?? [])]
      ),
    [JSON.stringify(inboxFilters), JSON.stringify(hideFilters)]
  );
  const hasFilters = filtersToHide.length < Object.keys(inboxFilters).length;

  return (
    <motion.div
      initial={{
        padding: hasFilters ? theme.spacing(1) : theme.spacing(0, 1),
        height: hasFilters ? 'auto' : 0,
      }}
      animate={{
        height: hasFilters ? 'auto' : 0,
        padding: hasFilters ? theme.spacing(1) : theme.spacing(0, 1),
      }}
      css={{
        backgroundColor: theme.colors.neutral10,
        overflow: 'hidden',
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
      }}
    >
      {!filtersToHide.includes('statuses') &&
        inboxFilters.statuses.map((status) => (
          <Chip.Removable
            key={status}
            css={filterChipStyles}
            onClick={() =>
              setInboxFilters({ ...inboxFilters, statuses: inboxFilters.statuses.filter((val) => val !== status) })
            }
          >
            {statusLabels[status]}
          </Chip.Removable>
        ))}
      {!filtersToHide.includes('tags') &&
        inboxFilters.tags.map((tagId) => {
          const tag = tagsListQuery.data?.tags.find((t) => t.id === tagId);
          if (!tag) return null;

          return (
            <Chip.Removable
              key={tagId}
              css={filterChipStyles}
              leftElement={<Icon name='label-small' />}
              onClick={() => setInboxFilters({ ...inboxFilters, tags: inboxFilters.tags.filter((id) => id !== tagId) })}
            >
              {tag.name}
            </Chip.Removable>
          );
        })}
      {!filtersToHide.includes('locationPhones') &&
        inboxFilters.locationPhones.map((locationPhone) => {
          const matchingLocationPhone = allLocationPhones.find((entry) => entry.locationPhone === locationPhone);
          if (!matchingLocationPhone) return null;

          return (
            <Chip.Removable
              key={locationPhone}
              css={filterChipStyles}
              leftElement={<Icon name='fax-number' />}
              onClick={() =>
                setInboxFilters({
                  ...inboxFilters,
                  locationPhones: inboxFilters.locationPhones.filter((phone) => phone !== locationPhone),
                })
              }
              tooltipLabel={formatPhoneNumber(locationPhone, true)}
            >
              {matchingLocationPhone?.displayName || formatPhoneNumber(matchingLocationPhone?.locationPhone, true)}
            </Chip.Removable>
          );
        })}
    </motion.div>
  );
};

const filterChipStyles = css({
  backgroundColor: theme.colors.primary50,
  color: theme.colors.white,
  width: 'max-content',
  maxWidth: 'none',
  svg: {
    color: theme.colors.white,
  },
});
